import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { Hexagon, Mail, ArrowUpRight, Smartphone, Send } from 'lucide-react';

const Footer = ({ scrollToContact }) => {
  const { t, language } = useLanguage();

  const quickLinks = [
    { key: 'services', href: '#services' },
    { key: 'packages', href: '#packages' },
    { key: 'testimonials', href: '#testimonials' },
    { key: 'contact', href: '#contact' },
  ];

  return (
    <footer className="bg-[#050505] border-t border-white/10" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <a href="#" className="flex items-center gap-3 mb-6">
              <div className="relative">
                <Hexagon className="w-10 h-10 text-emerald-500" strokeWidth={1.5} />
                <span className="absolute inset-0 flex items-center justify-center text-white font-bold text-sm">360</span>
              </div>
              <div>
                <span className="text-white font-bold text-lg tracking-tight">360Gaming</span>
                <span className="text-emerald-500 font-bold text-lg tracking-tight"> Solutions</span>
              </div>
            </a>
            <p className="text-white/50 max-w-sm mb-6 leading-relaxed">
              {t(translations.footer.tagline)}
            </p>
            <div className="flex flex-col gap-3">
              <a 
                href="mailto:b2b@360gamingsolutions.com"
                className="inline-flex items-center gap-2 text-emerald-500 hover:text-emerald-400 transition-colors"
              >
                <Mail className="w-5 h-5" />
                b2b@360gamingsolutions.com
              </a>
              <a 
                href="https://t.me/+306970384280"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-emerald-500 hover:text-emerald-400 transition-colors"
                data-testid="telegram-link"
              >
                <Send className="w-5 h-5" />
                Telegram
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-6">
              {t(translations.footer.quickLinks)}
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.key}>
                  <a 
                    href={link.href}
                    className="footer-link flex items-center gap-1 group"
                    data-testid={`footer-link-${link.key}`}
                  >
                    {t(translations.nav[link.key])}
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* CTA */}
          <div>
            <h4 className="text-white font-semibold mb-6">
              {t(translations.footer.contactUs)}
            </h4>
            <p className="text-white/50 text-sm mb-4">
              {language === 'en' 
                ? 'Ready to elevate your operations?' 
                : 'Έτοιμοι να αναβαθμίσετε τις λειτουργίες σας;'
              }
            </p>
            <button
              onClick={() => scrollToContact()}
              className="btn-primary text-sm"
              data-testid="footer-cta"
            >
              {t(translations.hero.cta)}
            </button>

            {/* BetGuard App Download */}
            <div className="mt-8">
              <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Smartphone className="w-4 h-4 text-yellow-500" />
                BetGuard App
              </h4>
              <p className="text-white/50 text-xs mb-3">
                {language === 'en' ? 'Download coming soon' : 'Σύντομα διαθέσιμο'}
              </p>
              <div className="flex gap-3">
                {/* Android */}
                <a 
                  href="#" 
                  onClick={(e) => e.preventDefault()}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 hover:border-emerald-500/30 transition-all group"
                  data-testid="download-android"
                  title="Coming Soon"
                >
                  <svg className="w-5 h-5 text-emerald-500" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.523 15.341c-.5 0-.91.41-.91.91s.41.91.91.91.91-.41.91-.91-.41-.91-.91-.91zm-11.046 0c-.5 0-.91.41-.91.91s.41.91.91.91.91-.41.91-.91-.41-.91-.91-.91zm11.4-6.744l1.86-3.22c.1-.18.04-.4-.14-.5s-.4-.04-.5.14l-1.88 3.26c-1.4-.64-2.96-1-4.62-1s-3.22.36-4.62 1l-1.88-3.26c-.1-.18-.32-.24-.5-.14s-.24.32-.14.5l1.86 3.22c-3.14 1.7-5.28 4.86-5.6 8.54h22.4c-.32-3.68-2.46-6.84-5.6-8.54h-.38zm-10.4 5.94c-.5 0-.91-.41-.91-.91s.41-.91.91-.91.91.41.91.91-.41.91-.91.91zm7.32 0c-.5 0-.91-.41-.91-.91s.41-.91.91-.91.91.41.91.91-.41.91-.91.91z"/>
                  </svg>
                  <span className="text-xs text-white/70 group-hover:text-white">Android</span>
                </a>
                
                {/* iOS */}
                <a 
                  href="#" 
                  onClick={(e) => e.preventDefault()}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 hover:border-emerald-500/30 transition-all group"
                  data-testid="download-ios"
                  title="Coming Soon"
                >
                  <svg className="w-5 h-5 text-white/70 group-hover:text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <span className="text-xs text-white/70 group-hover:text-white">iOS</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} 360Gaming Solutions. {t(translations.footer.rights)}
          </p>
          <div className="flex items-center gap-6 text-sm text-white/40">
            <a href="#" className="hover:text-white/60 transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-white/60 transition-colors">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
